import React from 'react';
import { css } from 'linaria';
import { styled } from 'linaria/react';
import Carousel from 'my-react-carousel';
import { useQuery } from 'react-apollo';
import { useIntl } from '@jetshop/intl';
import { Above } from '@jetshop/ui/Breakpoints';
import MaxWidth from '../../Layout/MaxWidth';
import SectionWrapper from '../../Layout/SectionWrapper';
import LoadingPage from '../../LoadingPage';
import { H3, H4 } from '../../ui/Typography';
import { theme } from '../../Theme';
import FeaturedProductsQuery from './FeaturedProductsQuery.gql';
import FeaturedProductCard from '../../Storyblok/ui/components/FeaturedProductCard';
import { ReactComponent as Arrow } from '../../../svg/chevron.svg';

const StyledMaxWidth = styled(MaxWidth)`
  ${theme.below.sm} {
    padding: 0rem;
  }

  ${H4} {
    ${theme.below.sm} {
      padding: 1rem;
    }
  }
`;

const ProductGrid = css`
  margin-top: 0.75rem;

  ${theme.above.sm} {
    margin-top: 1.25rem;
    position: relative;
  }

  .inner {
    display: grid;
    gap: 1rem;
    grid-auto-flow: column;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scroll-snap-type: x mandatory;
  }

  ${theme.below.sm} {
    .inner {
      &::after {
        content: '';
        width: 0.5rem;
      }

      .product-card {
        width: 60vw;
        scroll-snap-align: start;

        a {
          margin: 0 !important;
        }
      }
    }
  }

  button {
    -webkit-appearance: none;
    -moz-appearance: none;
    width: 30px;
    height: 30px;
    border: 0;
    border-radius: 0;
    background: ${theme.colors.white};
    padding: 0.5rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    top: calc(50% - 30px);
    z-index: 1;

    svg path {
      stroke: ${theme.colors.black};
    }

    &.previous {
      transform: rotate(90deg);
      left: -0.5rem;
    }

    &.next {
      transform: rotate(-90deg);
      right: -0.5rem;
    }
  }
`;

const FeaturedProducts = block => {
  if (!block.category_id) return null;
  const categoryId = parseInt(block.category_id);

  const intl = useIntl();
  const carousel = React.useRef(null);

  const { loading, data } = useQuery(FeaturedProductsQuery, {
    variables: { categoryId }
  });

  if (loading || !data) return <LoadingPage />;

  const products = data?.category?.products?.result;
  if (!products) return null;

  const filteredProducts = products.filter(
    product => product.images.length > 0
  );

  return (
    <SectionWrapper>
      <StyledMaxWidth>
        {block.title && (
          <Above breakpoint="sm">
            {match => (match ? <H3>{block.title}</H3> : <H4>{block.title}</H4>)}
          </Above>
        )}
        <div className={ProductGrid}>
          <Above breakpoint="sm">
            {match =>
              match ? (
                <>
                  <Carousel ref={carousel} slidesToShow={4}>
                    {filteredProducts.map(product => (
                      <div key={product.id}>
                        <FeaturedProductCard
                          product={product}
                          imageSizes={[2 / 3, 2 / 3, 1 / 4, 1 / 4, 285]}
                        />
                      </div>
                    ))}
                  </Carousel>
                  <button
                    aria-label={intl('Prev')}
                    className="previous"
                    onClick={() => carousel.current.previous()}
                  >
                    <Arrow />
                  </button>
                  <button
                    aria-label={intl('Next')}
                    className="next"
                    onClick={() => carousel.current.next()}
                  >
                    <Arrow />
                  </button>
                </>
              ) : (
                <div className="inner">
                  {products.map(product => (
                    <FeaturedProductCard
                      key={product.id}
                      product={product}
                      imageSizes={[2 / 3, 2 / 3, 1 / 4, 1 / 4, 285]}
                    />
                  ))}
                </div>
              )
            }
          </Above>
        </div>
      </StyledMaxWidth>
    </SectionWrapper>
  );
};

export default FeaturedProducts;
